// Exports de la supervision (M24, lot L6-A) : chaque vue s'exporte avec les mêmes filtres, permissions et
// colonnes que son écran ; le fichier indique vue, périmètre, période, sources, date de calcul et
// couverture. Les vues financières n'apparaissent qu'avec le droit financier.
import { INDICATEURS_SUPERVISION, ficheIndicateur } from '@cc/contrats';
import { Bouton, Selection } from '@cc/ui';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { useMessages } from '../../commun/Messages';
import { messageErreur } from '../../api/erreurs';
import { exporter, useRepertoire, type Filtres } from './api';
import { FiltresTerritoire, NavigationSupervision, provincesDu } from './Communs';
import { moisCourant } from './logique';
import { useDroitFinancier } from './PageIndicateurs';
import styles from '../achats/achats.module.css';
import propres from './supervision.module.css';

const VUES = [
  { vue: 'repertoire', libelle: 'Répertoire des collectivités', periode: false, financier: false },
  { vue: 'deploiement', libelle: 'Déploiement et modules', periode: false, financier: false },
  { vue: 'activite', libelle: 'Activité agrégée par collectivité', periode: true, financier: false },
  { vue: 'comparaison', libelle: 'Comparaison d\'un indicateur', periode: true, financier: false },
  { vue: 'finances', libelle: 'Grandes masses financières', periode: true, financier: true },
  { vue: 'competences', libelle: 'Compétences et moyens transférés', periode: false, financier: false },
] as const;

export function PageExports() {
  const droit = useDroitFinancier();
  const { afficher } = useMessages();
  const mois = moisCourant();
  const [filtres, setFiltres] = useState<Filtres>({ type: 'commune', mois_debut: mois, mois_fin: mois });
  const [indicateur, setIndicateur] = useState('DOS-10');
  const tout = useRepertoire({});
  const { provinces, imposee } = provincesDu(tout.data?.perimetre, tout.data?.elements ?? []);
  const indicateurs = INDICATEURS_SUPERVISION.filter((i) => droit || !i.financier);
  const instantane = INDICATEURS_SUPERVISION.find((i) => i.code === indicateur)?.nature === 'instantane';
  const lancer = async (vue: (typeof VUES)[number], format: 'csv' | 'xlsx') => {
    const avecPeriode = vue.periode && !(vue.vue === 'comparaison' && instantane);
    try {
      await exporter(vue.vue, format, {
        province: filtres.province,
        type: filtres.type,
        mois_debut: avecPeriode ? filtres.mois_debut : undefined,
        mois_fin: avecPeriode ? filtres.mois_fin : undefined,
        indicateur: vue.vue === 'comparaison' ? indicateur : undefined,
      });
    } catch (e) {
      afficher('erreur', messageErreur(e));
    }
  };
  return (
    <>
      <EnTetePage etiquette="Supervision" titre="Exports" description="Mêmes filtres, permissions et colonnes que les écrans. Chaque fichier indique ses sources, sa période, sa date de calcul et sa couverture ; chaque export est journalisé." />
      <NavigationSupervision actif="exports" />
      <FiltresTerritoire filtres={filtres} onChange={setFiltres} avecPeriode provinces={provinces} imposee={imposee} />
      <Selection libelle="Indicateur de la comparaison" options={indicateurs.map((i) => ({ valeur: i.code, libelle: `${i.code} ${ficheIndicateur(i.code)?.libelle ?? ''}` }))} value={indicateur} onChange={(e) => setIndicateur(e.target.value)} />
      {VUES.filter((v) => droit || !v.financier).map((v) => (
        <section key={v.vue} className={styles.bloc} aria-labelledby={`export-${v.vue}`}>
          <h2 id={`export-${v.vue}`}>{v.libelle}</h2>
          <p className={propres.mention}>{v.periode ? 'Période : mois choisis ci-dessus.' : 'Sans période : état au dernier calcul.'}</p>
          <div className={styles.actes}>
            <Bouton icone={Download} onClick={() => void lancer(v, 'csv')}>{`CSV : ${v.libelle}`}</Bouton>
            <Bouton icone={Download} onClick={() => void lancer(v, 'xlsx')}>{`XLSX : ${v.libelle}`}</Bouton>
          </div>
        </section>
      ))}
      {!droit ? <p className={propres.mention}>Les grandes masses financières ne sont pas proposées : votre institution ne dispose pas du droit financier.</p> : null}
    </>
  );
}
